import i18n from "i18next";
import { faEllipsisVertical, faPen } from "@fortawesome/pro-regular-svg-icons";
import { ReclamationView } from "src/api/generated/erp/db/types/tables/reclamationView.ts";
import { getReclamationStateLabel } from "src/api/generated/erp/db/types/enums/reclamationState.ts";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { openFormOnDialog } from "src/components/common/dialogs/formDialog/openFormOnDialog.ts";
import { LabelValueViewItem } from "src/components/common/misc/LabelValueView.tsx";
import { ObjectInfoView } from "src/components/common/misc/ObjectInfoView.tsx";
import { AavoMenuButton } from "src/components/common/menu/AavoMenuButton.tsx";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { ReclamationForm } from "src/components/views/erp/reclamation/ReclamationForm.tsx";
import { ReclamationContextMenu } from "src/components/views/erp/reclamation/ReclamationContextMenu.tsx";
import { formatIsoString } from "src/utils/dayjsUtils.ts";
import { formatNumber } from "src/utils/numberUtils.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface ReclamationInfoViewProps {
	reclamation: ReclamationView;
	refreshData: () => Promise<unknown>;
}

export const ReclamationInfoView = ({ reclamation, refreshData }: ReclamationInfoViewProps) => {
	const { openDialog } = useGenericDialog();
	const hasPermission = useHasPermission();
	const canManageReclamations = hasPermission(Permissions.Reclamations.MANAGE);

	return (
		<ObjectInfoView
			title={nullIfBlank(reclamation.title) ?? `${i18n.t("reclamation")} ${reclamation.reclamationId}`}
			subtitleItems={[
				`${i18n.t("reclamation")} ${reclamation.reclamationId}`,
				reclamation.reclamationState != null && getReclamationStateLabel(reclamation.reclamationState),
				reclamation.reclamationCategoryName,
			]}
			keyFacts={[
				{ label: i18n.t("responsible_person"), value: reclamation.responsiblePersonUserName },
				{ label: i18n.t("worker"), value: reclamation.workerUserName },
				{ label: i18n.t("planned_completed_date"), value: formatDateTime(reclamation.plannedCompletedDate) },
			]}
			actions={
				<>
					{canManageReclamations && (
						<AsyncButton
							icon={faPen}
							tooltip={i18n.t("edit")}
							size={"small"}
							onClick={() => {
								openFormOnDialog({
									openDialog,
									component: ReclamationForm,
									agentContext: {
										key: defineAgentUiContextKey(
											"erp.reclamation.reclamation.reclamationInfoView.formDialog",
										),
										data: {
											reclamationId: reclamation.reclamationId,
											customerOrderId: reclamation.customerOrderId ?? null,
										},
										pathSegments: [`${i18n.t("reclamation")} ${reclamation.reclamationId}`],
									},
									props: {
										reclamationId: reclamation.reclamationId,
										customerOrderId: reclamation.customerOrderId ?? undefined,
									},
									title: i18n.t("reclamation"),
									size: "xl",
									onSubmit: async () => {
										await refreshData();
									},
								});
							}}
						/>
					)}
					<AavoMenuButton
						icon={faEllipsisVertical}
						tooltip={i18n.t("more")}
						size={"small"}
						menuContent={
							<ReclamationContextMenu
								row={reclamation}
								allSelectedRows={[reclamation]}
								refreshData={refreshData}
							/>
						}
					/>
				</>
			}
			items={getReclamationInfoItems(reclamation)}
		/>
	);
};

const getReclamationInfoItems = (reclamation: ReclamationView): LabelValueViewItem[] => [
	{
		type: "section",
		label: i18n.t("general"),
		fields: [
			{
				label: i18n.t("site"),
				value: reclamation.siteName,
			},
			{
				label: i18n.t("round"),
				value: reclamation.reclamationRoundName,
			},
			{
				label: i18n.t("subject_code"),
				value: nullIfBlank(reclamation.subjectCode),
			},
			{
				label: i18n.t("cause"),
				value: reclamation.reclamationCauseName,
			},
			{
				label: i18n.t("handling_method"),
				value: reclamation.handlingMethodName,
			},
			{
				label: i18n.t("location"),
				value: reclamation.reclamationLocationName,
			},
			{
				label: i18n.t("description"),
				value: nullIfBlank(reclamation.description),
				multiline: true,
			},
			{
				label: i18n.t("demand"),
				value: nullIfBlank(reclamation.demand),
				multiline: true,
			},
			{
				label: i18n.t("act"),
				value: nullIfBlank(reclamation.act),
				multiline: true,
			},
		],
	},
	{
		type: "section",
		label: i18n.t("planning"),
		fields: [
			{
				label: i18n.t("planned_start"),
				value: formatDateTime(reclamation.plannedStartAt),
			},
			{
				label: i18n.t("estimated_work_hours"),
				value: formatHours(reclamation.estimatedWorkMinutes),
			},
		],
	},
	{
		type: "section",
		label: i18n.t("customer"),
		fields: [
			{
				label: i18n.t("customer_order"),
				value: reclamation.customerOrderLongName,
			},
			{
				label: i18n.t("complainer"),
				value: nullIfBlank(reclamation.complainerName),
			},
			{
				label: i18n.t("complainer_phone"),
				value: nullIfBlank(reclamation.complainerPhone),
			},
			{
				label: i18n.t("complainer_email"),
				value: nullIfBlank(reclamation.complainerEmail),
			},
		],
	},
	{
		type: "section",
		label: i18n.t("address"),
		fields: [
			{
				label: i18n.t("street_address"),
				value: nullIfBlank(reclamation.address.address_1),
			},
			{
				label: i18n.t("address_2"),
				value: nullIfBlank(reclamation.address.address_2),
			},
			{
				label: i18n.t("postal_code"),
				value: nullIfBlank(reclamation.address.postalCode),
			},
			{
				label: i18n.t("city"),
				value: nullIfBlank(reclamation.address.city),
			},
			{
				label: i18n.t("country"),
				value: nullIfBlank(reclamation.address.country),
			},
		],
	},
];

const formatDateTime = (value: string | null | undefined) => (value == null ? "-" : formatIsoString(value, "L LT"));

const formatHours = (minutes: number | null | undefined) =>
	minutes == null ? "-" : `${formatNumber(minutes / 60, { minDigits: 1, maxDigits: 1 })} h`;

const nullIfBlank = (value: string | null | undefined) => (value == null || value.trim() === "" ? null : value);
